import { useState } from 'react';
import { Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from '@posthog/react';
import type { Visibility } from '../api/generated/types.gen';
import type { StudyVisibilityToggleSource } from '../hooks/useStudyVisibilityToggle';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/SimpleTooltip';
import { ShareDialog } from './ShareDialog';
import { ScopeIndicator } from './ScopeIndicator';

interface StudyVisibilityControlProps {
  studyId: string;
  visibility: Visibility;
  size?: 'sm' | 'md';
  source?: StudyVisibilityToggleSource;
  /**
   * The current viewer's role on this study ("owner"|"editor"|"commenter"|
   * "viewer"|null for anonymous public reads). The Share button's management
   * affordance (opening `ShareDialog`) is owner-only — when omitted (existing
   * call sites that don't pass it yet), the button still renders so
   * behaviour doesn't regress for callers not yet updated.
   */
  myRole?: string | null;
}

/**
 * Owns the sharing entry point for a study: a read-only scope indicator
 * (lock/building/globe icon) plus a "Share" button that opens `ShareDialog`
 * (Google-Drive-style per-person invites + general access). Mirrors
 * `GeometryVisibilityControl`.
 */
export default function StudyVisibilityControl({
  studyId,
  visibility,
  size = 'md',
  source,
  myRole,
}: StudyVisibilityControlProps) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [dialogOpen, setDialogOpen] = useState(false);
  const canManage = myRole === undefined || myRole === 'owner';

  return (
    <div className="flex items-center gap-1.5">
      <ScopeIndicator visibility={visibility} size={size} />
      {canManage && (
        <SimpleTooltip content={t('compCommon.shareDialog.shareButton')}>
          <Button
            variant="outline"
            size="sm"
            className={size === 'sm' ? 'h-6 px-2 text-xs' : undefined}
            onClick={(e) => {
              e.stopPropagation();
              posthog.capture('share_dialog_opened', {
                resource_type: 'study',
                resource_id: studyId,
                ...(source && { source }),
              });
              setDialogOpen(true);
            }}
          >
            <Share2 className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
            {size === 'sm' ? (
              <span className="sr-only">
                {t('compCommon.shareDialog.shareButton')}
              </span>
            ) : (
              t('compCommon.shareDialog.shareButton')
            )}
          </Button>
        </SimpleTooltip>
      )}
      <ShareDialog
        resourceType="study"
        resourceId={studyId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
